import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

export default function ContactPage() {
  useSEO({
    title: 'Gratis Offerte Aanvragen | Contact DUC - Inkopen uit China',
    description: 'Vraag een gratis en vrijblijvende prijsindicatie aan voor inkopen uit China. Binnen 24 uur reactie van DUC. Product sourcing, private label en DDP levering.',
    keywords: 'gratis offerte inkopen China, prijsindicatie import China, contact sourcing agent DUC, offerte aanvragen China producten',
    canonical: `${SITE_URL}/contact`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact & Offerte Aanvragen — DUC Direct Uit China',
        description: 'Vraag een gratis en vrijblijvende prijsindicatie aan voor inkopen uit China. Binnen 24 uur reactie van DUC.',
        url: `${SITE_URL}/contact`,
        inLanguage: 'nl-NL',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${SITE_URL}/#business`,
        name: 'Direct Uit China (DUC)',
        description: 'Sourcing agent voor goedkoop inkopen uit China. Product sourcing, private label, DDP levering en importbemiddeling voor bedrijven en particulieren in Nederland.',
        url: SITE_URL,
        telephone: '+31624359162',
        email: 'Info@directuitchina.nl',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'NL',
          addressLocality: 'Nederland',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+31624359162',
          contactType: 'customer service',
          availableLanguage: ['Dutch', 'English'],
          contactOption: 'TollFree',
        },
        sameAs: [
          'https://www.instagram.com/directuitchina/',
          'http://linkedin.com/company/direct-uit-china',
        ],
      },
    ],
  });

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name) {
        if (el.type === 'checkbox') {
          if ((el as HTMLInputElement).checked) data.append(el.name, el.value);
        } else {
          data.append(el.name, el.value);
        }
      }
    }

    setStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d72h9viaegmdqoca7uc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const contactInfo = [
    {
      icon: 'ri-whatsapp-line',
      label: 'WhatsApp',
      value: '+31 6 24 35 91 62',
      href: 'https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China.',
    },
    {
      icon: 'ri-mail-line',
      label: 'E-mail',
      value: 'Info@directuitchina.nl',
      href: 'mailto:Info@directuitchina.nl',
    },
    {
      icon: 'ri-instagram-line',
      label: 'Instagram',
      value: '@directuitchina',
      href: 'https://www.instagram.com/directuitchina/',
    },
    {
      icon: 'ri-linkedin-fill',
      label: 'LinkedIn',
      value: 'Direct uit China',
      href: 'http://linkedin.com/company/direct-uit-china',
    },
    {
      icon: 'ri-time-line',
      label: 'Reactietijd',
      value: 'Binnen 24 uur',
      href: null,
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Nav */}
      <header className="bg-[#0A1628] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-28 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/15bdf5788918520e40e960585d4ea965/3037d4ed8e2d758e66815cdcd69eb3cd.png"
              alt="DUC - Direct uit China"
              className="h-[100px] w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/70 hover:text-[#3B9EE8] text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Terug naar home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0A1628] pb-20 pt-[100px] sm:pt-[116px] lg:pt-[136px]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-semibold mb-6"
          >
            <i className="ri-mail-send-line" />
            Neem contact op
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-4xl md:text-5xl font-bold mb-4 leading-tight"
          >
            Wij helpen je graag
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-white/55 text-lg max-w-xl mx-auto"
          >
            Heb je een vraag, wil je een offerte of gewoon even sparren? Vul het formulier in — we reageren binnen 24 uur.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Contact info */}
            <div className="lg:col-span-1">
              <h2 className="text-[#0A1628] font-bold text-xl mb-6">Direct contact</h2>
              <div className="flex flex-col gap-4 mb-10">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#0A1628]/8">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#3B9EE8]/10 text-[#3B9EE8] flex-shrink-0">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <div>
                      <p className="text-[#0A1628]/50 text-xs mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="nofollow noreferrer"
                          className="text-[#0A1628] font-semibold text-sm hover:text-[#3B9EE8] transition-colors cursor-pointer"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-[#0A1628] font-semibold text-sm">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-5 bg-[#0A1628] rounded-xl">
                <p className="text-[#3B9EE8] font-semibold text-sm mb-2">Snelste reactie?</p>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  Stuur ons direct een WhatsApp. Shubam reageert doorgaans binnen enkele uren.
                </p>
                <a
                  href="https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China."
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BC5A] text-white font-semibold text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base" />
                  App ons nu
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-[#0A1628]/8 p-8">
                <h2 className="text-[#0A1628] font-bold text-xl mb-6">Stuur ons een bericht</h2>

                {status === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-4">
                      <i className="ri-check-line text-2xl" />
                    </div>
                    <h3 className="text-[#0A1628] font-bold text-lg mb-2">Bericht verzonden!</h3>
                    <p className="text-[#0A1628]/55 text-sm">We reageren zo snel mogelijk, uiterlijk binnen 24 uur.</p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="mt-6 text-[#3B9EE8] text-sm hover:underline cursor-pointer"
                    >
                      Nieuw bericht sturen
                    </button>
                  </motion.div>
                ) : (
                  <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[#0A1628]/70 text-sm font-medium mb-1.5">
                          Voornaam <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="voornaam"
                          required
                          placeholder="Jan"
                          className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8]/60 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-[#0A1628]/70 text-sm font-medium mb-1.5">
                          Achternaam <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="achternaam"
                          required
                          placeholder="Jansen"
                          className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8]/60 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#0A1628]/70 text-sm font-medium mb-1.5">
                        E-mailadres <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="jan@bedrijf.nl"
                        className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8]/60 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-[#0A1628]/70 text-sm font-medium mb-1.5">
                        Telefoonnummer
                      </label>
                      <input
                        type="tel"
                        name="telefoon"
                        placeholder="+31 6 12 34 56 78"
                        className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8]/60 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-[#0A1628]/70 text-sm font-medium mb-1.5">
                        Onderwerp <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="onderwerp"
                        required
                        className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#3B9EE8]/60 transition-colors cursor-pointer"
                      >
                        <option value="">Selecteer een onderwerp</option>
                        <option value="Offerte aanvragen">Offerte aanvragen</option>
                        <option value="Product sourcing">Product sourcing</option>
                        <option value="Private label / maatwerk">Private label / maatwerk</option>
                        <option value="Levering & logistics">Levering &amp; logistics</option>
                        <option value="Algemene vraag">Algemene vraag</option>
                        <option value="Samenwerking">Samenwerking</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[#0A1628]/70 text-sm font-medium mb-1.5">
                        Jouw bericht <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="bericht"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Beschrijf je vraag of aanvraag zo concreet mogelijk. Wat zoek je? Hoeveel stuks? Welk product?"
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8]/60 transition-colors resize-none"
                      />
                      <p className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-red-500' : 'text-[#0A1628]/40'}`}>
                        {charCount}/500 tekens
                      </p>
                    </div>

                    {status === 'error' && (
                      <p className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                        Er is iets misgegaan. Probeer het opnieuw of stuur ons een WhatsApp.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full bg-[#3B9EE8] hover:bg-[#2D85C5] disabled:opacity-60 text-white font-bold text-sm py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {status === 'sending' ? 'Verzenden...' : 'Verstuur bericht'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple footer */}
      <footer className="bg-[#050E1A] py-8 text-center">
        <p className="text-white/30 text-xs">
          &copy; {new Date().getFullYear()} Direct uit China (DUC). Alle rechten voorbehouden. &nbsp;
          <Link to="/privacybeleid" className="hover:text-white/60 transition-colors">Privacybeleid</Link>
          &nbsp;·&nbsp;
          <Link to="/algemene-voorwaarden" className="hover:text-white/60 transition-colors">Algemene Voorwaarden</Link>
          &nbsp;·&nbsp;
          <Link to="/faq" className="hover:text-white/60 transition-colors">FAQ</Link>
        </p>
      </footer>
    </div>
  );
}
